import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { formatMarginPct } from '../../../quote-builder';
import { subtotalForMargin } from '../../../quote-builder/distributeToTarget';
import { DiscountSection } from './DiscountSection';

// PT-31 (2026-07-27): inline click-to-edit for the Gross Margin and Bid
// Subtotal rows. Renders a value that becomes an <input> when clicked; commits
// on Enter or blur, cancels on Escape. Bidirectional round-trip is handled by
// the parent's onCommit — the input just captures the raw string and hands it
// back parsed. Kept in this file (not extracted) because the two callsites
// have distinct value-formatters and the coupling is one-way.
function InlineEditableValue({
  value,
  formatDisplay,
  parse,
  onCommit,
  editable,
  ariaLabel,
  className,
  displayClassName,
  inputClassName,
  style,
  inputStyle,
  // IDI-361: while typing, show what the OTHER field becomes (GM% <-> Bid
  // Subtotal move each other). Returns a string, or null for no preview.
  renderPreview,
  readOnlyTitle,
  // IDI-534: called with the raw draft when it is negative or unparseable,
  // so the panel can say why nothing happened (it used to be a silent no-op).
  onInvalid,
  // IDI-534: a change order's net can be a credit.
  allowNegative = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  // IDI-534: the string the input opened with. A commit of that same value
  // (click in, click away) must NOT re-price: GM% opens with toFixed(1) of
  // the realized margin, so committing it re-priced every line by the
  // rounding and marked them all price_override.
  const shownRef = useRef('');

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (!editable) {
    return (
      <span className={displayClassName} style={style} title={readOnlyTitle}>
        {formatDisplay(value)}
      </span>
    );
  }

  const commit = () => {
    setEditing(false);
    const typed = String(draft ?? '').trim();
    const shown = String(shownRef.current ?? '').trim();
    // Unchanged (or emptied): just close the editor.
    if (typed === shown || typed === '') return;
    const parsed = parse(draft);
    if (parsed === null || parsed === undefined || !Number.isFinite(parsed) || (parsed < 0 && !allowNegative)) {
      if (typeof onInvalid === 'function') onInvalid(draft);
      return;
    }
    // Same number in a different spelling ("30" vs "30.0"): still unchanged.
    const shownParsed = parse(shown);
    if (shownParsed != null && Number.isFinite(shownParsed) && shownParsed === parsed) return;
    onCommit(parsed);
  };

  if (editing) {
    const preview = typeof renderPreview === 'function' ? renderPreview(draft) : null;
    return (
      <span className="inline-flex flex-col items-end">
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
        }}
        className={inputClassName}
        style={inputStyle}
      />
      {preview && (
        <span className="text-[10px] mt-0.5" data-testid="target-preview" style={{ color: 'var(--color-text-secondary)' }}>
          {preview}
        </span>
      )}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        const shown = formatDisplay(value, { forEdit: true });
        shownRef.current = shown;
        setDraft(shown);
        setEditing(true);
      }}
      aria-label={ariaLabel}
      className={className}
      style={style}
      title="Click to edit"
    >
      {formatDisplay(value)}
    </button>
  );
}

/**
 * EstimatePanel — Sticky right-rail with Cost Breakdown + Estimate.
 *
 * Phase A6 spec (updated iter18 PR-01):
 * - Cost Breakdown: Material / Labor / Other, each with % (hidden when totalCost === 0) + Total Cost
 * - Estimate reading order: Gross Margin (hero) · Margin · Bid Subtotal · Discount · Tax (Edit) · Bid Total
 *   Bid Total is the terminal / heaviest row (2px top border).
 * - IDI-309 / IDI-361: Gross Margin % and Bid Subtotal are editable and move
 *   each other; Margin $ is read-only. A commit calls onApplyTarget once with
 *   the whole-quote subtotal; the page re-prices eligible lines (see
 *   quote-builder/distributeToTarget.js). No standing override, and no
 *   "Quote margin X% applies to items without their own margin" subtitle:
 *   that figure was the residue of the old overall_margin write (IDI-312).
 *
 * Margin $, Tax and Bid Total are READ-ONLY (M-5).
 * Panel is sticky (scrolls with viewport).
 */

export default function EstimatePanel({
  summary,
  // `overallMargin` is no longer read here (IDI-312): the subtitle that
  // rendered it as a setting is gone. Callers may still pass it.
  onEditTax,
  discounts = [],
  onAddDiscount,
  onUpdateDiscount,
  onRemoveDiscount,
  // Iter-18: Cancel + Finalize footer buttons (per QB-1 wire, under sticky estimate)
  onCancel,
  onFinalize,
  finalizeLabel = 'Review and send',
  finalizeDisabled = false,
  finalizeHint,
  // PR-13 (2026-07-22): Save-as-Template lives in the rail per QB-2 v1.2.
  // Visible on empty quotes but disabled until a line item OR option exists.
  onSaveAsTemplate,
  saveAsTemplateDisabled = false,
  // IDI-309 / IDI-361: Gross Margin % and Bid Subtotal edits. Called once per
  // commit with `{ subtotalCents, marginPct? }` (the whole-figure target). May
  // return `{ ok:false, message }` to refuse; the panel shows the message.
  // Replaces onEditOverallMargin, which wrote the lowest margin tier and so
  // was overruled by every line with its own margin.
  onApplyTarget,
  // When set, both fields render read-only with this explanation (e.g. every
  // line has its own category margin).
  targetEditDisabledReason = null,
  // An error to show under the fields (e.g. from the page's own refusal).
  targetError = null,
  // Cost the card's Gross Margin % is measured against. Defaults to the cost
  // implied by the summary (bidSubtotal * (1 - blendedMargin)).
  targetCostBasis,
  // IDI-534: a change order's Bid Subtotal is its NET change, which can be a
  // credit. When set, a negative subtotal is a legal entry (the engine
  // accepts or refuses it with a reason); a quote never allows one.
  allowNegativeSubtotal = false,
}) {
  const {
    costBreakdown: breakdown,
    blendedMargin,
    bidSubtotal,
    taxAmount,
    // IDI-458: optional caption under the Tax row (change-order tax basis).
    taxNote,
    bidTotal,
    marginDollars,
  } = summary || {};

  const totalCost = breakdown?.totalCost || 0;
  const showPct = totalCost > 0;

  // §1b final (2026-08-07): the panel is a REPORT of what the engine
  // resolved per-line. It never flattens.
  //
  // Operator precedence ruling (2026-08-07):
  //   HIGHEST  line-item / category margin  (Catalog & Inventory)
  //     beats  customer-specific margin
  //     beats  master default (overall) margin  LOWEST
  //
  // `overallMargin` is the LOWEST tier — a fallback for lines with no
  // category margin. It has NEVER been architecturally correct to apply it
  // flat to every line as a display-time override; that mechanism inverted
  // the precedence. Per-line categoryMargin wins, always.
  //
  // Removed: overrideActive / displayMarginPct / displayBidSubtotal etc.
  // The margin engine's `blendedMargin` / `bidSubtotal` / `bidTotal` /
  // `taxAmount` / `marginDollars` are the truth-of-record.
  const displayMarginPct = blendedMargin;
  const displayBidSubtotal = bidSubtotal;
  const displayMarginDollars = marginDollars;
  const displayTaxAmount = taxAmount ?? 0;
  const displayBidTotal = bidTotal ?? 0;

  // IDI-555: a Gross Margin % message shows inside the Gross Margin box; a
  // Bid Subtotal message shows under Bid Subtotal. One message at a time:
  // showing either clears the other.
  const [gmError, setGmError] = useState(null);
  const [subtotalError, setSubtotalError] = useState(null);
  const showGmError = (msg) => { setGmError(msg); setSubtotalError(null); };
  const showSubtotalError = (msg) => { setSubtotalError(msg); setGmError(null); };

  const canEditTarget = typeof onApplyTarget === 'function' && !targetEditDisabledReason;
  const marginCost = Number.isFinite(Number(targetCostBasis)) && targetCostBasis != null
    ? Math.round(Number(targetCostBasis))
    : Math.round((displayBidSubtotal || 0) * (1 - (displayMarginPct || 0)));

  const parseDollars = (str) => {
    const cleaned = String(str).replace(/[$,\s]/g, '');
    if (cleaned === '') return null;
    // IDI-534: Number() not parseFloat(): "12abc" is unparseable, not 12.
    const asDollars = Number(cleaned);
    if (!Number.isFinite(asDollars)) return null;
    if (asDollars < 0 && !allowNegativeSubtotal) return null;
    return Math.round(asDollars * 100);
  };
  const parsePct = (str) => {
    const cleaned = String(str).replace(/[%\s]/g, '');
    if (cleaned === '') return null;
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n < 0) return null;
    return n / 100;
  };
  // IDI-534: a negative or unparseable entry says so instead of silently
  // doing nothing.
  const invalidPct = (draft) => {
    const n = Number(String(draft).replace(/[%\s]/g, ''));
    showGmError(Number.isFinite(n) && n < 0
      ? 'Gross margin cannot be negative.'
      : 'Enter a gross margin as a number, e.g. 30.');
  };
  const invalidDollars = (draft) => {
    const n = Number(String(draft).replace(/[$,\s]/g, ''));
    showSubtotalError(Number.isFinite(n) && n < 0
      ? 'Bid subtotal cannot be negative.'
      : 'Enter a bid subtotal as a dollar amount, e.g. 12500.00.');
  };

  // IDI-534: margin (as a %) a subtotal implies against the card's cost.
  const pctAt = (cents) => (cents > 0 ? formatMarginPct((cents - marginCost) / cents) : null);

  const applyTarget = (subtotalCents, marginPct) => {
    if (!canEditTarget) return;
    const result = onApplyTarget({ subtotalCents, ...(marginPct != null ? { marginPct } : {}) });
    if (result && result.ok === false) {
      const showError = marginPct != null ? showGmError : showSubtotalError;
      // IDI-534: the user typed a %, so say the reachable range in %. The
      // engine's dollar range converts through the same cost basis.
      if (marginPct != null && Number.isFinite(result.minCents) && marginCost > 0) {
        const lo = pctAt(result.minCents);
        const hi = result.maxCents != null ? pctAt(result.maxCents) : null;
        if (lo != null) {
          const range = hi != null ? `${lo} to ${hi}` : `at least ${lo}`;
          showError(`That margin cannot be reached. Reachable gross margin: ${range}.`);
          return;
        }
      }
      showError(result.message || 'That target cannot be reached.');
    } else {
      setGmError(null);
      setSubtotalError(null);
    }
  };

  const commitMarginPct = (gm) => {
    if (!(gm >= 0 && gm < 1)) { showGmError('Gross margin must be at least 0% and below 100%'); return; }
    if (marginCost === 0) { showGmError('Add item costs before setting a margin'); return; }
    const subtotalCents = subtotalForMargin(marginCost, gm);
    if (subtotalCents == null) { showGmError('Could not compute a bid subtotal from that margin'); return; }
    applyTarget(subtotalCents, gm);
  };

  const commitSubtotal = (subtotalCents) => {
    if (marginCost === 0 && subtotalCents !== 0) { showSubtotalError('Add item costs before setting a bid subtotal'); return; }
    applyTarget(subtotalCents, null);
  };

  const shownError = subtotalError || targetError;
  return (
    <div
      // Q-11 (2026-08-18): `self-start` prevents the grid parent's default
      // align-items:stretch from stretching this rail to the full items-
      // column height. Without it, "Review and send" at the bottom of the
      // rail scrolls off-screen on tall quotes because sticky positioning
      // has nothing to stick against when the rail is already the full page
      // height. `max-h` + `overflow-y-auto` let the rail scroll internally
      // if it ever grows taller than the viewport (rare, but possible with
      // many labor/tax breakdowns).
      className="sticky top-6 self-start max-h-[calc(100vh-3rem)] overflow-y-auto space-y-4"
      style={{ minWidth: '280px' }}
    >
      {/* Cost Breakdown Card */}
      <Card className="p-4 space-y-3">
        <h4
          className="text-xs font-semibold uppercase tracking-wide"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          Cost Breakdown
        </h4>

        <div className="space-y-2">
          <CostRow
            label="Material"
            amount={breakdown?.material || 0}
            pct={breakdown?.materialPct || 0}
            showPct={showPct}
          />
          <CostRow
            label="Labor"
            amount={breakdown?.labor || 0}
            pct={breakdown?.laborPct || 0}
            showPct={showPct}
          />
          <CostRow
            label="Other"
            amount={breakdown?.other || 0}
            pct={breakdown?.otherPct || 0}
            showPct={showPct}
          />
        </div>

        <div
          className="border-t pt-2 flex items-center justify-between"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
            Total Cost
          </span>
          <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
            {formatCurrency(totalCost)}
          </span>
        </div>
      </Card>

      {/* Estimate Card */}
      <Card className="p-4 space-y-3">
        <h4
          className="text-xs font-semibold uppercase tracking-wide"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          Estimate
        </h4>

        <div className="space-y-2">
          {/* §6.1 (2026-07-29): Gross Margin now uses a neutral emphasis block
              (--neutral-50 surface, standard text color). Prior --red-50/red-700
              collided with the red primary/destructive tone — pink read as an
              error state. The percentage is derived-read-only per §3. */}
          <div
            className="p-3 -mx-1"
            data-testid="gross-margin-box"
            style={{ backgroundColor: 'var(--neutral-50)', borderRadius: 'var(--radius-md)' }}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                Gross Margin
              </span>
              {/* IDI-361: Gross Margin % is editable. Commit converts the
                  typed margin into the whole-quote subtotal it implies and
                  hands that to onApplyTarget. */}
              <span
                // Q-08 (§3ao, 2026-08-18): title branches on marginSource, the
                // stable discriminant set upstream.
                title={
                  summary?.marginSource === 'migrated-bid'
                    ? 'From the original bid record. Line costs may differ.'
                    : undefined
                }
              >
                <InlineEditableValue
                  value={displayMarginPct != null ? displayMarginPct : 0}
                  editable={canEditTarget && !!displayBidSubtotal}
                  ariaLabel="Gross margin percent"
                  readOnlyTitle={targetEditDisabledReason || undefined}
                  formatDisplay={(v, opts) => {
                    if (opts && opts.forEdit) return ((v || 0) * 100).toFixed(1);
                    return displayBidSubtotal ? formatMarginPct(v || 0) : '—';
                  }}
                  parse={parsePct}
                  onInvalid={invalidPct}
                  renderPreview={(draft) => {
                    const gm = parsePct(draft);
                    if (gm == null || !(gm < 1) || marginCost === 0) return null;
                    const s = subtotalForMargin(marginCost, gm);
                    return s == null ? null : `Bid subtotal ${formatCurrency(s)}`;
                  }}
                  onCommit={commitMarginPct}
                  className="text-lg font-bold tabular-nums underline decoration-dotted underline-offset-2"
                  displayClassName="text-lg font-bold tabular-nums"
                  inputClassName="text-lg font-bold tabular-nums text-right w-24 bg-white border rounded px-2 py-1"
                  style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-display)' }}
                  inputStyle={{ color: 'var(--color-text-primary)', borderColor: 'var(--color-border)' }}
                />
              </span>
            </div>
            {summary?.marginSource === 'migrated-bid' && (
              <p className="text-[10px] mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                Margin from the original bid record. Edit line items to re-price.
              </p>
            )}
            {gmError && (
              <p className="text-xs mt-1" role="alert" style={{ color: 'var(--color-error)' }}>{gmError}</p>
            )}
          </div>

          {/* IDI-361: Margin $ is READ-ONLY. It is the consequence of the
              Gross Margin % and Bid Subtotal, not a third input. */}
          <div className="flex items-center justify-between">
            <span
              className="text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Margin
            </span>
            <span
              className="text-sm tabular-nums"
              data-testid="margin-dollars"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {formatCurrency(displayMarginDollars || 0)}
            </span>
          </div>

          {/* IDI-309 / IDI-361: Bid Subtotal is editable. A commit re-prices
              the eligible lines ONCE so the whole subtotal lands on the typed
              figure (onApplyTarget). Nothing is written to overall_margin. */}
          <div className="flex items-center justify-between">
            <span
              className="text-sm font-semibold"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Bid Subtotal
            </span>
            <InlineEditableValue
              value={displayBidSubtotal || 0}
              editable={canEditTarget}
              ariaLabel="Bid subtotal in dollars"
              readOnlyTitle={targetEditDisabledReason || undefined}
              formatDisplay={(v, opts) => {
                if (opts && opts.forEdit) {
                  return ((v || 0) / 100).toFixed(2);
                }
                return formatCurrency(v || 0);
              }}
              parse={parseDollars}
              onInvalid={invalidDollars}
              allowNegative={allowNegativeSubtotal}
              renderPreview={(draft) => {
                const cents = parseDollars(draft);
                if (cents == null || cents <= 0 || marginCost === 0) return null;
                return `Gross margin ${formatMarginPct((cents - marginCost) / cents)}`;
              }}
              onCommit={commitSubtotal}
              className="text-sm font-semibold tabular-nums underline decoration-dotted underline-offset-2"
              displayClassName="text-sm font-semibold tabular-nums"
              inputClassName="text-sm font-semibold tabular-nums text-right w-32 bg-white border rounded px-2 py-1"
              style={{ color: 'var(--color-text-primary)' }}
              inputStyle={{ color: 'var(--color-text-primary)', borderColor: 'var(--color-border)' }}
            />
          </div>
          {typeof onApplyTarget === 'function' && targetEditDisabledReason && (
            <p className="text-xs mt-1" data-testid="target-readonly-reason" style={{ color: 'var(--color-text-secondary)' }}>
              {targetEditDisabledReason}
            </p>
          )}
          {shownError && (
            <p className="text-xs mt-1" role="alert" style={{ color: 'var(--color-error)' }}>{shownError}</p>
          )}

          {/* Discounts (REQ-WF-01 / LAT-04) */}
          {(onAddDiscount || discounts.length > 0) && (
            <div className="border-t pt-2" style={{ borderColor: 'var(--color-border)' }}>
              <DiscountSection
                discounts={discounts}
                onAdd={onAddDiscount}
                onUpdate={onUpdateDiscount}
                onRemove={onRemoveDiscount}
                subtotal={(displayBidSubtotal || 0) / 100}
              />
            </div>
          )}

          {/* Tax (with Edit button) */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Tax</span>
              {onEditTax && (
                <button
                  type="button"
                  onClick={onEditTax}
                  className="text-xs font-medium underline"
                  style={{ color: 'var(--color-action-primary)' }}
                >
                  Edit
                </button>
              )}
            </div>
            <span className="text-sm tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
              {formatCurrency(displayTaxAmount)}
            </span>
          </div>

          {/* IDI-458: an optional basis note under the Tax row. The
              change-order builder uses it to say the rate came from the job's
              SIGNED quote (ruled 2026-10-06), or that it isn't known — a bare
              $0.00 presented as fact is how a taxable change order gets
              under-billed. The quote builder passes nothing and is unchanged. */}
          {taxNote && (
            <div className="flex justify-end -mt-1 mb-1">
              <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                {taxNote}
              </span>
            </div>
          )}

          {/* Bid Total — terminal / heaviest row (read-only, M-5). iter18 PR-01: 2px top border. */}
          <div
            className="flex items-center justify-between"
            style={{
              borderTop: '2px solid var(--color-text-primary)',
              marginTop: '8px',
              paddingTop: '9px',
            }}
          >
            <span
              className="text-sm font-bold"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Bid Total
            </span>
            <span
              className="text-lg font-bold tabular-nums"
              style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-display)' }}
            >
              {formatCurrency(displayBidTotal)}
            </span>
          </div>
        </div>
      </Card>

      {/* PR-13 (2026-07-22): rail CTAs per QB-2 v1.2 — primary is full-width
          "Review and send", Save-as-Template is full-width secondary below,
          Cancel is a plain link. Drops the prior card-boxed pair. */}
      {(onCancel || onFinalize || onSaveAsTemplate) && (
        <Card className="p-3">
          <div className="flex flex-col gap-2">
            {onFinalize && (
              <Button
                onClick={onFinalize}
                disabled={finalizeDisabled}
                className="w-full"
              >
                {finalizeLabel}
              </Button>
            )}
            {finalizeDisabled && finalizeHint && (
              <p className="text-xs text-center" style={{ color: 'var(--color-text-secondary)' }}>
                {finalizeHint}
              </p>
            )}
            {onSaveAsTemplate && (
              <Button
                variant="secondary"
                onClick={onSaveAsTemplate}
                disabled={saveAsTemplateDisabled}
                className="w-full"
              >
                Save as template
              </Button>
            )}
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="text-xs font-medium hover:underline mt-1"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Cancel
              </button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function CostRow({ label, amount, pct, showPct = true }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
        {showPct && (
          <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
            {Math.round(pct * 100)}%
          </span>
        )}
      </div>
      <span className="text-sm tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
        {formatCurrency(amount)}
      </span>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars -- retained as a canonical reference row shape; consumers may resume once EstimatePanel round-trip work resumes
function EstimateRow({ label, value, bold, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span
        className={`text-sm ${bold ? 'font-medium' : ''}`}
        style={{ color: muted ? 'var(--color-text-secondary)' : 'var(--color-text-secondary)' }}
      >
        {label}
      </span>
      <span
        className={`text-sm tabular-nums ${bold ? 'font-semibold' : ''}`}
        style={{ color: muted ? 'var(--color-text-secondary)' : 'var(--color-text-primary)' }}
      >
        {value}
      </span>
    </div>
  );
}

function formatCurrency(cents) {
  const n = Number(cents) || 0;
  const negative = n < 0;
  const abs = Math.abs(n);
  const body = `$${(abs / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return negative ? `-${body}` : body;
}
